import { TimeDataType } from 'apis/types';

const BUSINESS_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const BUSINESS_TIME_PATTERN = /^(\d{2}):([0-5]\d)$/;
const CALENDAR_WEEKDAY_BITS = [2, 128, 64, 32, 16, 8, 4] as const;

export interface BusinessDateParts {
  year: number;
  month: number;
  day: number;
}

export type CalendarWeekdayBit = typeof CALENDAR_WEEKDAY_BITS[number];

const createUtcDate = ({ year, month, day }: BusinessDateParts): Date => {
  const date = new Date(0);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCFullYear(year, month - 1, day);
  return date;
};

export const parseBusinessDate = (value: string): BusinessDateParts | null => {
  const match = BUSINESS_DATE_PATTERN.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = createUtcDate({ year, month, day });

  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }

  return { year, month, day };
};

export const getCalendarWeekdayBit = (dateParts: BusinessDateParts): CalendarWeekdayBit => {
  return CALENDAR_WEEKDAY_BITS[createUtcDate(dateParts).getUTCDay()];
};

export const parseBusinessTimeToMinutes = (value: string): number | null => {
  const match = BUSINESS_TIME_PATTERN.exec(value);
  if (!match) return null;

  return Number(match[1]) * 60 + Number(match[2]);
};

export const isHolidayFromServer = (isHoliday: unknown): boolean => isHoliday === 1;

export const isValidServerTimeData = (value: unknown): value is TimeDataType => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;

  const candidate = value as Partial<TimeDataType>;
  return (
    typeof candidate.business_date === 'string' &&
    parseBusinessDate(candidate.business_date) !== null &&
    typeof candidate.tm_hh_mm === 'string' &&
    parseBusinessTimeToMinutes(candidate.tm_hh_mm) !== null
  );
};
